/* ==========================================================================
   STL 23 Sep 2026 — load behaviour for the header flyouts, the mobile drawer
   and the PDP gallery. Loaded with `defer` from layout/theme.liquid, straight
   after theme.js, so it runs in the same tick as Edge's own scripts.

   Three jobs, all measured on Working (see CLAUDE.md):

   1. NAV IMAGES. stl.css §9a keeps the mega-menu and drawer submenu panels out
      of rendering (`content-visibility: hidden`) so their 12 banner images —
      2,019KB, 62% of a PDP's image bytes — are not downloaded on page load.
      This warms them on the first sign the shopper is heading for the menu,
      which is earlier than the panel opening, so the menu is not blank.

   2. THE DEAD TAP. theme.js is `defer`, so a tap before it runs opens
      `<details>` without the `.menu-opening` class that draws the drawer.
      stl.css §9b makes the drawer follow `open` until this file says JS is
      ready; here we square up anything already open — including the height
      Edge would have measured — and hand back to Edge.

   3. PDP GALLERY. Slides 2+ ship with their URLs in `data-stl-src(set)` and
      are restored after `load` (or on the first touch, whichever comes first),
      so the shopper's connection spends the load on the image they can see.
      ========================================================================== */
(function () {
  'use strict';

  var root = document.documentElement;

  /* ---------- 1. Warm the flyout images ---------- */

  var warmed = false;
  var pageLoaded = document.readyState === 'complete';
  var warmPending = false;

  function warmNav() {
    if (warmed) return;

    /* Never during the page load itself — that is the whole point of §9a. A third
       party (a popup taking focus, a script's synthetic click) can trip a listener
       here while the page is still loading, and on 23 Sep 2026 one did: all twelve
       banners came down at 1.2s, before `load`. Intent noted, acted on at `load`. */
    if (!pageLoaded) {
      warmPending = true;
      return;
    }

    warmed = true;
    root.classList.add('stl-nav-warm');

    /* Making the panels renderable is not enough on its own: the browser does not
       re-run its lazy-load check just because a box appeared, and the drawer's
       submenus are parked off-screen anyway. So ask for the images outright.

       `responsive-image` (theme.js) takes `srcset` OFF a lazy image and keeps it in
       `originalSrcset` until the image intersects. Putting it back here, with `sizes`
       set to the width the banner is actually drawn at, means the browser fetches the
       same file the intersection path would have chosen — so when that path does run
       it changes nothing and there is no second download. */
    document.querySelectorAll(
      '.mega-menu__content img[loading="lazy"], .menu-drawer__submenu img[loading="lazy"]'
    ).forEach(function (img) {
      var width = Math.round(img.getBoundingClientRect().width);

      /* Only what this breakpoint actually draws. The header ships BOTH sets of
         markup: at desktop the drawer's copies are display:none, at mobile the
         mega-menu's are. A box of 0 means this copy is the other breakpoint's —
         fetching it wastes the bytes twice over, and with no width to go on the
         browser reads the authored `sizes` (52vw on the drawer's copies) against
         the DESKTOP viewport and asks for the 1000px master. */
      if (width === 0) return;

      var host = img.closest('responsive-image');
      var srcset = img.getAttribute('srcset') || (host && host.originalSrcset);

      if (srcset) {
        if (host && host.getAttribute('data-sizes') === 'true') {
          img.setAttribute('sizes', width + 'px');
        }
        img.setAttribute('srcset', srcset);
      }

      img.setAttribute('loading', 'eager');
    });
  }

  window.addEventListener('load', function () {
    pageLoaded = true;
    if (warmPending) warmNav();
  }, { once: true });

  /* The triggers are the two things that actually lead to a panel: the cursor entering
     the desktop nav, and the hamburger being tapped. The header as a whole is too broad
     — a tap on the cart or the search icon would pull ~820KB nobody asked for. */
  var warmTriggers = [
    document.querySelector('nav.header__inline-menu'),
    document.querySelector('#Details-menu-drawer-container > summary')
  ];

  warmTriggers.forEach(function (el) {
    if (!el) return;
    var warmOpts = { once: true, passive: true };
    el.addEventListener('pointerenter', warmNav, warmOpts);
    el.addEventListener('touchstart', warmNav, warmOpts);
    el.addEventListener('click', warmNav, warmOpts);
    el.addEventListener('focusin', warmNav, { once: true });
  });

  /* ---------- 2. Keep the drawer's height honest ---------- */

  /* Edge fixes the drawer's height from `window.innerHeight` at the moment its own
     summary handler runs (`--viewport-height`, read only by the menu drawer). Two ways
     that goes stale: Chrome Android grows the viewport when it hides the URL bar, and
     §9b now lets a tap that beat theme.js open the drawer without Edge's handler having
     run at all. Either way the drawer stops short of the bottom and a strip of the page
     shows under it, which reads as a line below Log in / Contact Us (Serena, 23 Sep). */
  function syncViewportHeight() {
    document.documentElement.style.setProperty('--viewport-height', window.innerHeight + 'px');
  }

  window.addEventListener('resize', syncViewportHeight, { passive: true });

  if (window.visualViewport) {
    window.visualViewport.addEventListener('resize', syncViewportHeight, { passive: true });
  }

  /* ---------- 3. Reconcile a drawer opened before theme.js ran ---------- */

  function markNavReady() {
    var container = document.getElementById('Details-menu-drawer-container');

    if (container && container.hasAttribute('open')) {
      /* The shopper opened it while stl.css §9b was drawing it. Give Edge the
         state it would have set itself, so the next tap closes rather than
         being swallowed. */
      warmNav();
      syncViewportHeight();
      container.classList.add('menu-opening');

      var summary = container.querySelector('summary');
      if (summary) summary.setAttribute('aria-expanded', 'true');

      var drawer = container.querySelector('header-drawer') || container.closest('header-drawer');
      var breakpoint = drawer && drawer.dataset ? drawer.dataset.breakpoint : null;
      if (breakpoint) document.body.classList.add('overflow-hidden-' + breakpoint);

      container.querySelectorAll('details[open]').forEach(function (details) {
        details.classList.add('menu-opening');

        var parent = details.closest('.has-submenu');
        if (parent) parent.classList.add('submenu-open');

        var detailsSummary = details.querySelector('summary');
        if (detailsSummary) detailsSummary.setAttribute('aria-expanded', 'true');
      });
    }

    root.classList.add('stl-nav-ready');
  }

  if (window.customElements && customElements.whenDefined) {
    customElements.whenDefined('header-drawer').then(markNavReady);
  } else {
    markNavReady();
  }

  /* ---------- 4. Restore the deferred gallery slides ---------- */

  var restored = false;

  function restoreDeferredImages() {
    if (restored) return;
    restored = true;

    document.querySelectorAll('img[data-stl-src]').forEach(function (img) {
      var srcset = img.getAttribute('data-stl-srcset');

      if (srcset) {
        img.setAttribute('srcset', srcset);
        img.removeAttribute('data-stl-srcset');
      }

      img.setAttribute('src', img.getAttribute('data-stl-src'));
      img.removeAttribute('data-stl-src');

      /* Eager, not lazy: the point is to have them cached BEFORE the shopper swipes.
         Inside a horizontal carousel a lazy image waits for the slide to be scrolled
         in, which is the moment we are trying to be ahead of. The page has finished
         loading by now, so this costs nothing that matters. */
      img.setAttribute('loading', 'eager');
    });
  }

  if (document.readyState === 'complete') {
    restoreDeferredImages();
  } else {
    window.addEventListener('load', restoreDeferredImages, { once: true });
  }

  /* A swipe (or any touch/click) before `load` means the shopper wants them NOW;
     the timer is the belt and braces if `load` never fires because a third-party
     request is still hanging. */
  document.addEventListener('pointerdown', restoreDeferredImages, { once: true, passive: true });
  setTimeout(restoreDeferredImages, 5000);

  /* ---------- 5. Quick buy: tapping the dimmed area closes it ---------- */

  /* Edge puts its close handler on `.product-quick-view__overlay` (z-index 1002), but
     the dimming a shopper actually taps is painted by `#product-quick-view` itself
     (rgba(0,0,0,.5), z-index 1003, pointer-events: all). The overlay never sees the
     tap, so on mobile the size sheet could only be closed with the X (Serena, 23 Sep).
     A tap that lands on the modal element itself — not on anything inside the white
     sheet, which is a descendant — is a tap outside the panel. */
  document.addEventListener('click', function (event) {
    var modal = document.getElementById('product-quick-view');

    if (!modal || event.target !== modal) return;
    if (!modal.classList.contains('quick-view-loaded')) return;

    var closeButton = modal.querySelector('.product-quick-view__close');
    if (closeButton) closeButton.click();
  });
})();
